import { useCallback, useEffect, useRef, useState } from 'react';
import { ApiError } from '@/shared/api/apiClient';
import { logger } from '@/shared/utils/logger';
import {
  cancelExport,
  createExport,
  describeExportError,
  errorCodeOf,
  fetchExport,
  fetchExportDownload,
  fetchExports,
  newIdempotencyKey,
} from '@/features/exports/services/exportsApi';
import { isActiveExport, type ExportRecord } from '@/features/exports/types';

/** Poll every 2 s for the first 30 s of watching an export, then 5 s, then 10 s at most. */
export function pollDelayMs(elapsedMs: number): number {
  if (elapsedMs < 30_000) return 2000;
  if (elapsedMs < 90_000) return 5000;
  return 10_000;
}
const POLL_MAX_MS = 10_000;
/** Never make someone wait longer than this on a Retry-After before they may try again. */
const MAX_COOLDOWN_S = 300;

export type ExportConflict = { exportId: string; videoId: string };

/** Whether the same attempt may safely be sent again: we do not know the server saw it. */
const isRetrySafe = (err: unknown): boolean =>
  err instanceof ApiError && (err.status === 0 || err.status === 429 || err.status >= 500);

const STATUS_ANNOUNCEMENT: Record<string, string> = {
  queued: 'Export is waiting to start.',
  processing: 'Export is converting.',
  completed: 'Your export is ready to download.',
  failed: 'The export failed.',
  cancelled: 'Export cancelled.',
  expired: 'The export has expired.',
};

const merge = (list: ExportRecord[], next: ExportRecord): ExportRecord[] =>
  list.some((e) => e.id === next.id) ? list.map((e) => (e.id === next.id ? next : e)) : [next, ...list];

/**
 * One video's exports: the list, creating, cancelling, downloading, and polling the
 * active one. Everything is inert unless `open` — closing the dialog stops the polling.
 */
export function useVideoExports(videoId: string, open: boolean) {
  const [exports, setExports] = useState<ExportRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [conflict, setConflict] = useState<ExportConflict | null>(null);
  const [coolingDown, setCoolingDown] = useState(false);
  const [busyItems, setBusyItems] = useState<Record<string, 'cancel' | 'download'>>({});
  const [itemErrors, setItemErrors] = useState<Record<string, string>>({});
  const [announcement, setAnnouncement] = useState('');

  const mounted = useRef(true);
  const busy = useRef(false);
  /* The key of the attempt in flight, or last failed in a way that may be retried. */
  const attempt = useRef<{ preset: string; key: string } | null>(null);
  const cooldownTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const openRef = useRef(open);
  openRef.current = open;

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      if (cooldownTimer.current) clearTimeout(cooldownTimer.current);
    };
  }, []);

  const announce = useCallback((text: string) => setAnnouncement(text), []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const list = await fetchExports(videoId);
      if (!mounted.current || !openRef.current) return;
      setExports(list.exports);
      setLoadError(null);
    } catch (err) {
      if (!mounted.current) return;
      logger.error('Failed to load exports', err);
      setLoadError("We couldn't load your earlier exports.");
    } finally {
      if (mounted.current) setLoading(false);
    }
  }, [videoId]);

  /* Load on open; forget everything on close so a reopen never shows stale state. */
  useEffect(() => {
    if (!open) {
      setExports([]);
      setLoadError(null);
      setCreateError(null);
      setConflict(null);
      setItemErrors({});
      setBusyItems({});
      setAnnouncement('');
      attempt.current = null;
      return;
    }
    void load();
  }, [open, load]);

  const active = exports.find((e) => e.video_id === videoId && isActiveExport(e)) ?? null;
  const activeId = active?.id ?? null;

  const applyUpdate = useCallback(
    (next: ExportRecord) => {
      setExports((prev) => {
        const before = prev.find((e) => e.id === next.id);
        if (before && before.status !== next.status) {
          const text = STATUS_ANNOUNCEMENT[next.status];
          if (text) announce(text);
        }
        return merge(prev, next);
      });
    },
    [announce],
  );

  /* ---- polling the active export ---- */
  useEffect(() => {
    if (!open || !activeId) return;
    let cancelled = false;
    let inflight = false;
    let failures = 0;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const started = Date.now();

    const schedule = () => {
      if (timer) clearTimeout(timer);
      if (cancelled || document.hidden) return;
      const delay = Math.min(pollDelayMs(Date.now() - started) * 2 ** failures, POLL_MAX_MS);
      timer = setTimeout(() => void tick(), delay);
    };

    const tick = async () => {
      if (cancelled || document.hidden || inflight) return;
      inflight = true;
      try {
        const next = await fetchExport(videoId, activeId);
        if (cancelled) return;
        failures = 0;
        applyUpdate(next);
        if (!isActiveExport(next)) return; // terminal: the effect re-runs and stops
      } catch (err) {
        if (cancelled) return;
        logger.error('Failed to poll an export', err);
        if (err instanceof ApiError && err.status === 404) {
          void load(); // it is gone (or the feature was switched off): re-read the list
          return;
        }
        failures = Math.min(failures + 1, 4);
      } finally {
        inflight = false;
      }
      schedule();
    };

    const onVisibility = () => {
      if (document.hidden) {
        if (timer) clearTimeout(timer);
      } else {
        void tick(); // catch up straight away; tick re-arms the timer
      }
    };

    document.addEventListener('visibilitychange', onVisibility);
    schedule();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [open, activeId, videoId, applyUpdate, load]);

  /* ---- actions ---- */
  const create = useCallback(
    async (presetKey: string) => {
      if (busy.current || coolingDown || activeId) return;
      busy.current = true;
      setCreating(true);
      setCreateError(null);
      setConflict(null);
      /* Same preset as the last attempt that may not have reached the server: same key,
         so a retry cannot make a second export. Anything else is a new attempt. */
      const current =
        attempt.current && attempt.current.preset === presetKey
          ? attempt.current
          : { preset: presetKey, key: newIdempotencyKey() };
      attempt.current = current;
      try {
        const record = await createExport(videoId, presetKey, current.key);
        attempt.current = null;
        if (!mounted.current) return;
        applyUpdate(record);
        announce(
          record.reused
            ? 'You already have this export. It is shown below.'
            : 'Export started. We will tell you when it is ready.',
        );
      } catch (err) {
        if (!isRetrySafe(err)) attempt.current = null;
        logger.error('Failed to create an export', err);
        if (!mounted.current) return;
        const code = errorCodeOf(err);
        if (err instanceof ApiError && code === 'export_in_progress') {
          const d = err.detail as { export_id?: string; video_id?: string };
          if (d.export_id) setConflict({ exportId: d.export_id, videoId: d.video_id ?? videoId });
          void load();
        }
        setCreateError(describeExportError(err));
        if (err instanceof ApiError && err.status === 429) {
          const seconds = Math.min(err.retryAfterSeconds ?? 0, MAX_COOLDOWN_S);
          if (seconds > 0) {
            setCoolingDown(true);
            cooldownTimer.current = setTimeout(() => {
              if (mounted.current) setCoolingDown(false);
            }, seconds * 1000);
          }
        }
      } finally {
        busy.current = false;
        if (mounted.current) setCreating(false);
      }
    },
    [activeId, announce, applyUpdate, coolingDown, load, videoId],
  );

  const setItemBusy = (id: string, value: 'cancel' | 'download' | null) =>
    setBusyItems((prev) => {
      const next = { ...prev };
      if (value) next[id] = value;
      else delete next[id];
      return next;
    });
  const setItemError = (id: string, message: string | null) =>
    setItemErrors((prev) => {
      const next = { ...prev };
      if (message) next[id] = message;
      else delete next[id];
      return next;
    });

  /** Cancels an export. `forVideoId` differs from this video for the "already running" case. */
  const cancel = useCallback(
    async (exportId: string, forVideoId: string = videoId) => {
      if (busyItems[exportId]) return;
      setItemBusy(exportId, 'cancel');
      setItemError(exportId, null);
      try {
        const record = await cancelExport(forVideoId, exportId);
        if (!mounted.current) return;
        if (forVideoId === videoId) applyUpdate(record);
        setConflict((c) => (c && c.exportId === exportId ? null : c));
        setCreateError(null);
        announce('Export cancelled.');
      } catch (err) {
        logger.error('Failed to cancel an export', err);
        if (!mounted.current) return;
        setItemError(exportId, describeExportError(err));
        if (err instanceof ApiError && err.status === 409) void load();
      } finally {
        if (mounted.current) setItemBusy(exportId, null);
      }
    },
    [announce, applyUpdate, busyItems, load, videoId],
  );

  /** Mints a fresh link and navigates to it, exactly as the page's own Download does. */
  const download = useCallback(
    async (exportId: string) => {
      if (busyItems[exportId]) return;
      setItemBusy(exportId, 'download');
      setItemError(exportId, null);
      try {
        const { url } = await fetchExportDownload(videoId, exportId);
        if (!url) throw new Error('No download URL returned');
        window.location.href = url;
      } catch (err) {
        logger.error('Failed to download an export', err);
        if (!mounted.current) return;
        setItemError(exportId, describeExportError(err));
        // Expired or gone: show its real state so "Create again" is offered.
        if (err instanceof ApiError && (err.status === 410 || err.status === 409)) void load();
      } finally {
        if (mounted.current) setItemBusy(exportId, null);
      }
    },
    [busyItems, load, videoId],
  );

  return {
    exports,
    loading,
    loadError,
    reload: load,
    active,
    creating,
    createError,
    conflict,
    coolingDown,
    create,
    cancel,
    download,
    busyItems,
    itemErrors,
    announcement,
  };
}
